import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Stack,
  Paper,
  Avatar,
  Divider,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  Skeleton,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import {
  PhotoCamera,
  Close,
  Public,
  People,
  Lock,
  ArrowDropDown,
  PersonRemove,
  PersonAdd,
  Settings,
} from "@mui/icons-material";
import { MentionsInput, Mention } from "react-mentions";
import axios from "axios";
import imageCompression from "browser-image-compression";

import { useAuth } from "../../../../context/AuthContext";
import { useSystems } from "../../../../context/SystemContext";
import EditPostModal from "./EditPostModal";
import PostCard from "./PostCard";
import { mentionsStylePost } from "./mentionsStyles";
import FriendSelectionDialog from "./FriendSelectionDialog";

// --- CONFIGURACIÓN DE PRIVACIDAD ---
const PRIVACY_OPTIONS = [
  { id: "public", label: "Público", sub: "Cualquiera dentro y fuera de la red", icon: <Public fontSize="small" /> },
  { id: "friends", label: "Amigos", sub: "Tus amigos en la plataforma", icon: <People fontSize="small" /> },
  { id: "friends_except", label: "Amigos, excepto...", sub: "No mostrar a algunos amigos", icon: <PersonRemove fontSize="small" /> },
  { id: "specific_friends", label: "Amigos concretos", sub: "Mostrar solo a algunos amigos", icon: <PersonAdd fontSize="small" /> },
  { id: "private", label: "Solo yo", sub: "Solo tú puedes verlo", icon: <Lock fontSize="small" /> },
  { id: "custom", label: "Personalizado", sub: "Incluir y excluir amigos y listas", icon: <Settings fontSize="small" /> },
];

const SocialManager = ({ userId: propsUserId, mode = "feed", isOwnProfile, highlightPostId }) => {
  const { user } = useAuth();
  const { activeProfile: headerActiveProfile } = useSystems();
  const viewerId = user?.user_id || user?.id;
  const userId = propsUserId || viewerId;

  const [dbUserData, setDbUserData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [isDbLoading, setIsDbLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [privacy, setPrivacy] = useState(PRIVACY_OPTIONS[0]);
  const [activeProfile, setActiveProfile] = useState(null);
  const manuallyChangedProfileRef = useRef(false);
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const [openPrivacyModal, setOpenPrivacyModal] = useState(false);
  const [openFriendSelector, setOpenFriendSelector] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [customConfig, setCustomConfig] = useState({ allow: [], deny: [] });
  const [activePost, setActivePost] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editImageUrl, setEditImageUrl] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const API_POSTS = "https://apiweb.hitpoly.com/ajax/postController.php";
  const API_SOCIAL = "https://apiweb.hitpoly.com/ajax/apiSocialController.php";
  const API_PERFIL = "https://apiweb.hitpoly.com/ajax/ObtenerPerfilController.php";

  // 1. OBTENER DATOS DIRECTO DE LA BD
  const fetchRealUserData = useCallback(async () => {
    if (!viewerId) return;
    setIsDbLoading(true);
    try {
      const res = await axios.get(`${API_PERFIL}?user_id=${viewerId}&t=${new Date().getTime()}`);

      if (res.data.success && res.data.datos) {
        const d = res.data.datos;
        const principal = d.usuario_principal || {};

        const nombre = principal.nombre || "";
        const apellido = principal.apellido || "";
        const full = `${nombre} ${apellido}`.trim();

        setDbUserData({
          fullName: full || "Usuario Hitpoly",
          avatar: principal.avatar || d.perfil_general?.profile_picture || "",
          idCargo: principal.id_cargo,
          perfiles_publicos: d.perfiles_publicos || [],
        });

      } else {
         console.warn("[SocialManager] ⚠️ Error al obtener datos. Response:", res.data);
      }
    } catch (e) {
      console.error("[SocialManager] fetchRealUserData error:", e);
    } finally {
      setIsDbLoading(false);
    }
  }, [viewerId]);

  const extractPosts = (data) => {
    if (Array.isArray(data)) return data;
    if (data?.data && Array.isArray(data.data)) return data.data;
    if (data?.posts && Array.isArray(data.posts)) return data.posts;
    if (data?.result && Array.isArray(data.result)) return data.result;
    return [];
  };

  const fetchPosts = useCallback(async () => {
    if (!userId) {
      console.warn("[SocialManager] No userId available");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const action = mode === "profile" ? "myPosts" : "feed";
      const url = `${API_SOCIAL}?action=${action}&userId=${userId}&viewerId=${viewerId}`;
      const res = await axios.get(url);
      let postsData = extractPosts(res.data);
      postsData = postsData.map(p => {
        const key = `post_author_${String(p.id)}`;
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const override = JSON.parse(stored);
            if (override) {
              return { ...p, user_avatar: override.avatar, user_full_name: override.nombre, nombre_real: override.nombre };
            }
          }
        } catch (e) {}
        return p;
      });
      if (highlightPostId) {
        const idx = postsData.findIndex(p => String(p.id) === String(highlightPostId));
        if (idx > 0) {
          const [post] = postsData.splice(idx, 1);
          postsData = [post, ...postsData];
        }
      }
      setPosts(postsData);
    } catch (e) {
      console.error("[SocialManager] fetchPosts error:", e?.response?.data || e.message || e);
    } finally {
      setLoading(false);
    }
  }, [userId, viewerId, mode, API_SOCIAL]);

  const handleUpdateSinglePost = useCallback(async (postId) => {
      try {
        const action = mode === "profile" ? "myPosts" : "feed";
        const res = await axios.get(`${API_SOCIAL}?action=${action}&userId=${userId}&viewerId=${viewerId}`);
        const postsData = extractPosts(res.data);
        const updatedPost = postsData.find(p => String(p.id) === String(postId));
        if (updatedPost) {
          const key = `post_author_${String(postId)}`;
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const override = JSON.parse(stored);
              if (override) {
                updatedPost.user_avatar = override.avatar;
                updatedPost.user_full_name = override.nombre;
                updatedPost.nombre_real = override.nombre;
              }
            }
          } catch (e) {}
          setPosts(prev => prev.map(post => String(post.id) === String(postId) ? updatedPost : post));
        }
      } catch (e) {
        console.error("[SocialManager] handleUpdateSinglePost error:", e?.response?.data || e.message || e);
      }
    }, [userId, viewerId, mode, API_SOCIAL]
  );

  useEffect(() => {
    fetchPosts();
    fetchRealUserData();
  }, [fetchPosts, fetchRealUserData]);

  useEffect(() => {
    try {
      const cached = localStorage.getItem('cached_profiles');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setProfiles(parsed);
          return;
        }
      }
    } catch (e) {}
    if (dbUserData?.perfiles_publicos?.length > 0) {
      setProfiles(dbUserData.perfiles_publicos.map(p => ({
        id: p.id,
        type: p.type || 'enterprise',
        name: p.nombre || p.name || '',
        label: p.label || 'Perfil',
        avatar: p.avatar || ''
      })));
    }
  }, [dbUserData]);

  useEffect(() => {
    if (headerActiveProfile && !manuallyChangedProfileRef.current) {
      setActiveProfile({
        id: headerActiveProfile.id,
        type: headerActiveProfile.type,
        nombre: headerActiveProfile.name || headerActiveProfile.label,
        avatar: headerActiveProfile.avatar
      });
    }
  }, [headerActiveProfile]);

  const queryUsersWrapper = useCallback(async (query, callback, onlyFriends = false) => {
      try {
        const typeParam = onlyFriends ? "friends" : "all";
        const search = query?.trim() || '';
        const url = `${API_POSTS}?search=${encodeURIComponent(search)}&type=${typeParam}&userId=${viewerId}`;
        const res = await axios.get(url);
        if (res.data && Array.isArray(res.data)) {
          const mapped = res.data.filter(u => String(u.id) !== String(viewerId));
          callback(mapped);
        } else { callback([]); }
      } catch (e) {
        console.error("[SocialManager] queryUsers error:", e?.response?.data || e.message || e);
        callback([]);
      }
    }, [viewerId, API_POSTS]
  );

  const queryHashtags = useCallback(async (query, callback) => {
    if (!query || query.length < 1) return callback([]);
    try {
      const url = `${API_POSTS}?search_hashtags=${encodeURIComponent(query)}`;
      const res = await axios.get(url);
      if (res.data && Array.isArray(res.data)) {
        callback(res.data.map(h => ({ id: h.id, display: h.name, post_count: h.post_count })));
      } else { callback([]); }
    } catch (e) {
      console.error("[SocialManager] queryHashtags error:", e?.response?.data || e.message || e);
      callback([]);
    }
  }, [API_POSTS]);

  const handleImageUpload = async (file, isForEdit = false) => {
    if (!file) return;
    setIsUploading(true);
    try {
      // 1. Obtener autenticación dinámica
      const authRes = await axios.get(`${API_PERFIL.replace('ObtenerPerfilController.php', 'imagekit_auth.php')}?userId=${viewerId}`);
      const { token, expire, signature, publicKey } = authRes.data;

      // 2. Comprimir imagen
      const options = { maxSizeMB: 0.8, maxWidthOrHeight: 2000, useWebWorker: true, fileType: "image/webp" };
      const compressedFile = await imageCompression(file, options);

      // 3. Subir a ImageKit
      const formData = new FormData();
      formData.append("file", compressedFile);
      formData.append("fileName", `post_${viewerId}_${Date.now()}.webp`);
      formData.append("publicKey", publicKey);
      formData.append("signature", signature);
      formData.append("expire", expire);
      formData.append("token", token);
      formData.append("useUniqueFileName", "true");
      formData.append("folder", `/posts/${viewerId}`);

      const resIK = await axios.post("https://upload.imagekit.io/api/v1/files/upload", formData);
      
      if (resIK.data.url) {
        if (isForEdit) setEditImageUrl(resIK.data.url);
        else setImageUrl(resIK.data.url);
      }
    } catch (e) { 
      console.error("[SocialManager] Image upload error:", e?.response?.data || e.message || e); 
    } finally { 
      setIsUploading(false); 
    }
  };

  const handlePublish = async () => {
    if (!content.trim() && !imageUrl) return;
    let privacyListToSend = privacy.id === "custom" 
        ? { allow: customConfig.allow.map(f => f.id), deny: customConfig.deny.map(f => f.id) }
        : selectedFriends.map(f => f.id);

    try {
      const res = await axios.post(API_POSTS, {
        userId: viewerId,
        content,
        imageUrl,
        privacy_type: privacy.id,
        privacy_list: privacyListToSend,
        author_profile_id: activeProfile?.id || null,
        author_profile_type: activeProfile?.type || null,
      });
      if (res.data.success) {
        setContent(""); setImageUrl(""); setPrivacy(PRIVACY_OPTIONS[0]);
        setSelectedFriends([]); setCustomConfig({ allow: [], deny: [] });
        const raw = res.data.post || res.data.data || res.data.result;
        const newPost = raw ? (Array.isArray(raw) ? raw[0] : raw) : null;
        if (newPost && activeProfile) {
          newPost.user_avatar = activeProfile.avatar;
          newPost.user_full_name = activeProfile.nombre;
          newPost.nombre_real = activeProfile.nombre;
          setPosts(prev => [newPost, ...prev]);
        } else if (activeProfile) {
          const postId = String(res.data.postId || Date.now());
          const manualPost = {
            id: postId,
            user_id: viewerId,
            user_avatar: activeProfile.avatar,
            user_full_name: activeProfile.nombre,
            nombre_real: activeProfile.nombre,
            apellido_real: '',
            content: content,
            image_url: imageUrl || null,
            privacy_type: privacy.id,
            created_at: new Date().toISOString(),
            likes_count: 0,
            comments_count: 0,
          };
          setPosts(prev => [manualPost, ...prev]);
          try {
            const key = `post_author_${postId}`;
            localStorage.setItem(key, JSON.stringify({ avatar: activeProfile.avatar, nombre: activeProfile.nombre }));
          } catch (e) {}
        } else {
          fetchPosts();
        }
      } else {
        console.warn("[SocialManager] Publish response not success:", res.data);
      }
    } catch (e) {
      console.error("[SocialManager] handlePublish error:", e?.response?.data || e.message || e);
    }
  };

  const handleUpdatePost = async (file = null, onlyUpload = false) => {
    if (onlyUpload && file) return handleImageUpload(file, true);
    try {
      const res = await axios.post(API_POSTS, {
        postId: activePost.id,
        userId: viewerId,
        content: editContent,
        imageUrl: editImageUrl,
        privacy_type: activePost.privacy_type,
        privacy_list: activePost.privacy_detail,
      });
      if (res.data.success) {
        setIsEditing(false); setActivePost(null);
        handleUpdateSinglePost(activePost.id);
      } else {
        console.warn("[SocialManager] Update post response not success:", res.data);
      }
    } catch (e) {
      console.error("[SocialManager] handleUpdatePost error:", e?.response?.data || e.message || e);
    }
  };

  const handleDeletePost = async () => {
    if (!activePost || isDeleting) return;
    setIsDeleting(true);
    try {
      const res = await axios.delete(API_POSTS, { params: { postId: activePost.id, userId: viewerId } });
      if (res.data?.success) {
        setOpenDeleteDialog(false);
        setPosts(prev => prev.filter(p => p.id !== activePost.id));
        setActivePost(null);
      } else {
        console.warn("[SocialManager] Delete post response not success:", res.data);
      }
    } catch (e) {
      console.error("[SocialManager] handleDeletePost error:", e?.response?.data || e.message || e);
    } finally { setIsDeleting(false); }
  };

  return (
    <Box sx={{ bgcolor: "#f0f2f5", minHeight: "100vh" }}>
      {(mode === "feed" || isOwnProfile || String(userId) === String(viewerId)) && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
            {isDbLoading ? (
              <>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ width: "100%" }}><Skeleton width="40%" /><Skeleton width="20%" /></Box>
              </>
            ) : (
              <>
                 <Avatar src={activeProfile ? activeProfile.avatar : dbUserData?.avatar} />
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      onClick={(e) => profiles.length > 0 && setAnchorElProfile(e.currentTarget)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: profiles.length > 0 ? 'pointer' : 'default' }}
                    >
                      <Typography sx={{ fontWeight: 600 }}>
                        {activeProfile ? activeProfile.nombre : dbUserData?.fullName}
                      </Typography>
                      {profiles.length > 0 && (
                        <ArrowDropDown sx={{ fontSize: 18, color: 'primary.main' }} />
                      )}
                      {activeProfile && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 600 }}>
                          {activeProfile.type === 'main' ? 'Perfil Principal' : activeProfile.type === 'professional' ? 'Perfil Profesional' : 'Perfil Empresa'}
                        </Typography>
                      )}
                    </Box>
                    <Menu
                          anchorEl={anchorElProfile}
                          open={Boolean(anchorElProfile)}
                          onClose={() => setAnchorElProfile(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          sx={{ zIndex: 99999 }}
                        >
                          {profiles.map(p => {
                            if (!p) return null;
                            const isMain = p.type === 'main';
                            const displayName = p.name || p.label || p.nombre || 'Perfil';
                            return (
                              <MenuItem 
                                key={`${p.type || 'unknown'}-${p.id || Math.random()}`}
                                onClick={() => { 
                                  manuallyChangedProfileRef.current = true;
                                  setActiveProfile({
                                    id: p.id,
                                    type: p.type,
                                    nombre: displayName,
                                    avatar: p.avatar
                                  }); 
                                  setAnchorElProfile(null); 
                                }}
                                selected={activeProfile?.id === p.id && activeProfile?.type === p.type}
                              >
                                <ListItemAvatar><Avatar src={p.avatar} sx={{ width: 24, height: 24 }}>{displayName?.charAt(0)}</Avatar></ListItemAvatar>
                                <ListItemText>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography variant="body2" fontWeight={activeProfile?.id === p.id && activeProfile?.type === p.type ? 700 : 400}>{displayName}</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'primary.main', fontWeight: 600 }}>{isMain ? 'Perfil Principal' : p.type === 'professional' ? 'Perfil Profesional' : 'Perfil Empresa'}</Typography>
                                  </Box>
                                </ListItemText>
                              </MenuItem>
                            );
                          })}
                        </Menu>
                    </Box>
                  <Button
                    size="small"
                    onClick={() => setOpenPrivacyModal(true)}
                    startIcon={privacy.icon}
                    endIcon={<ArrowDropDown />}
                    sx={{ bgcolor: "#e4e6eb", color: "#4b4c4f", textTransform: "none", borderRadius: 1.5 }}
                  >
                    {privacy.label}
                  </Button>
                </Box>
              </>
            )}
          </Stack>

          <MentionsInput
            value={content}
            onChange={(e) => setContent(e.target.value)}
            style={mentionsStylePost}
            placeholder="¿Qué estás pensando?"
          >
            <Mention
              trigger="@"
              data={(q, cb) => queryUsersWrapper(q, cb, false)}
              markup="@[__display__](__id__)"
              style={mentionsStylePost.mention}
            />
            <Mention
              trigger="#"
              data={queryHashtags}
              markup="#[__display__]"
              style={{ ...mentionsStylePost.mention, backgroundColor: '#3B207F20', color: 'transparent' }}
            />
          </MentionsInput>

          {imageUrl && (
            <Box sx={{ position: "relative", mt: 2 }}>
              <img src={imageUrl} width="100%" style={{ borderRadius: 8 }} alt="preview" />
              <IconButton onClick={() => setImageUrl("")} sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.6)", color: "white" }}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button startIcon={<PhotoCamera sx={{ color: "#45bd62" }} />} component="label" disabled={isUploading}>
              {isUploading ? "Subiendo..." : "Foto"}
              <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0])} />
            </Button>
            <Button variant="contained" onClick={handlePublish} disabled={(!content.trim() && !imageUrl) || isUploading}>
              Publicar
            </Button>
          </Box>
        </Paper>
      )}

      <Stack spacing={2}>
        {loading ? (
          <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              userId={viewerId}
              isHighlighted={String(post.id) === String(highlightPostId)}
              userAvatar={activeProfile ? activeProfile.avatar : dbUserData?.avatar}
              currentUserName={activeProfile ? activeProfile.nombre : (dbUserData?.fullName || '')}
              activeProfile={activeProfile}
              API_SOCIAL={API_SOCIAL}
              queryUsers={queryUsersWrapper}
              onLikeUpdate={() => handleUpdateSinglePost(post.id)}
              onEdit={(p) => { 
                setEditContent(p.content || ""); 
                setEditImageUrl(p.image_url || ""); 
                setActivePost(p); 
                setIsEditing(true); 
              }}
              onDelete={(p) => { 
                setActivePost(p); 
                setOpenDeleteDialog(true); 
              }}
            />
          ))
        ) : (
          <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>No hay publicaciones.</Typography>
        )}
      </Stack>

      {/* MODAL PRIVACIDAD */}
      <Dialog open={openPrivacyModal} onClose={() => setOpenPrivacyModal(false)} fullWidth maxWidth="sm" sx={{ zIndex: 99999 }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>Audiencia</DialogTitle>
        <Box sx={{ p: 1 }}>
          {PRIVACY_OPTIONS.map((opt) => (
            <MenuItem key={opt.id} onClick={() => { 
                setPrivacy(opt); setOpenPrivacyModal(false);
                if (opt.id === "friends_except" || opt.id === "specific_friends") setOpenFriendSelector(true);
            }} sx={{ py: 1.5 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: "#e4e6eb", color: "black" }}>{opt.icon}</Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.sub}</Typography>
                </Box>
              </Stack>
            </MenuItem>
          ))}
        </Box>
      </Dialog>

      <FriendSelectionDialog
        open={openFriendSelector}
        onClose={() => setOpenFriendSelector(false)}
        title={privacy.id === "friends_except" ? "Amigos excepto..." : "Amigos concretos"}
        queryUsers={(q, cb) => queryUsersWrapper(q, cb, true)}
        excludeIds={[viewerId]}
        initialSelection={selectedFriends}
        onConfirm={(list) => { setSelectedFriends(list); setOpenFriendSelector(false); }}
      />


      {isEditing && activePost && (
        <EditPostModal
          open={isEditing} onClose={() => setIsEditing(false)}
          content={editContent} setContent={setEditContent}
          imageUrl={editImageUrl} setImageUrl={setEditImageUrl}
          isUploading={isUploading} onUpdate={handleUpdatePost}
          queryUsers={(q, cb) => queryUsersWrapper(q, cb, false)}
        />
      )}

      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        sx={{ zIndex: 9999 }}
        PaperProps={{ sx: { zIndex: 10000, borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>¿Eliminar publicación?</DialogTitle>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={isDeleting}>Cancelar</Button>
          <Button onClick={handleDeletePost} color="error" variant="contained" disabled={isDeleting} sx={{ borderRadius: 1.5 }}>
            {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialManager;